import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';

interface VisaApplication {
  id: string;
  urgencyLevel: string;
  currentStatus: string;
  destination: string;
}

interface MarketplaceRecommendationsProps {
  application: VisaApplication;
}

interface ServiceRecommendation {
  id: string;
  type: 'insurance' | 'expedited' | 'document-prep' | 'legal-support';
  title: string;
  description: string;
  price: string;
  features: string[];
  urgent: boolean;
  provider: string;
  rating: number;
  ctaText: string;
  ctaLink: string;
  icon: string;
}

export default function MarketplaceRecommendations({ application }: MarketplaceRecommendationsProps) {
  const getRecommendations = (): ServiceRecommendation[] => {
    const recommendations: ServiceRecommendation[] = [];

    // Travel Insurance - Always recommended
    recommendations.push({
      id: 'rec-insurance',
      type: 'insurance',
      title: 'Comprehensive Travel Insurance',
      description: 'Protect your trip with medical coverage, cancellation protection, and emergency assistance',
      price: 'from £29.99',
      features: ['Medical coverage up to £1M', 'Trip cancellation', 'Lost baggage', '24/7 assistance'],
      urgent: false,
      provider: 'TravelGuard Pro',
      rating: 4.8,
      ctaText: 'Get Quote',
      ctaLink: '/marketplace?service=insurance',
      icon: 'ShieldCheckIcon'
    });

    // Expedited Service - High urgency or critical
    if (application.urgencyLevel === 'high' || application.urgencyLevel === 'critical') {
      recommendations.push({
        id: 'rec-expedited',
        type: 'expedited',
        title: 'Express Visa Processing',
        description: 'Fast-track your application with priority processing and embassy liaison',
        price: 'from £89.99',
        features: ['Priority processing', 'Embassy liaison', '48-hour turnaround', 'Status tracking'],
        urgent: true,
        provider: 'VisaExpress Global',
        rating: 4.9,
        ctaText: 'Apply Now',
        ctaLink: '/marketplace?service=expedited',
        icon: 'BoltIcon'
      });
    }

    // Document Preparation - Additional docs required
    if (application.currentStatus === 'additional-docs-required') {
      recommendations.push({
        id: 'rec-document',
        type: 'document-prep',
        title: 'Professional Document Preparation',
        description: 'Expert assistance with document verification, translation, and embassy formatting',
        price: 'from £49.99',
        features: ['Document review', 'Translation services', 'Embassy formatting', 'Same-day delivery'],
        urgent: true,
        provider: 'DocuPro Services',
        rating: 4.7,
        ctaText: 'Get Help',
        ctaLink: '/marketplace?service=document-prep',
        icon: 'DocumentCheckIcon'
      });
    }

    // Legal Support - Rejected or complex cases
    if (application.currentStatus === 'rejected' || application.urgencyLevel === 'critical') {
      recommendations.push({
        id: 'rec-legal',
        type: 'legal-support',
        title: 'Immigration Legal Consultation',
        description: 'Expert legal advice for complex visa cases, appeals, and embassy representation',
        price: 'from £149.99',
        features: ['Legal consultation', 'Appeal assistance', 'Embassy representation', 'Case review'],
        urgent: application.currentStatus === 'rejected',
        provider: 'Global Immigration Law',
        rating: 4.9,
        ctaText: 'Consult Now',
        ctaLink: '/marketplace?service=legal',
        icon: 'ScaleIcon'
      });
    }

    return recommendations;
  };

  const recommendations = getRecommendations();

  const typeColors = {
    insurance: 'from-sky-blue to-primary',
    expedited: 'from-coral to-error',
    'document-prep': 'from-warning to-accent',
    'legal-support': 'from-primary to-secondary'
  };

  return (
    <div className="bg-white rounded-xl shadow-brand p-6 border-2 border-border">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
          <Icon name="ShoppingBagIcon" size={20} className="text-white" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-bold text-xl text-foreground">
            Recommended Services
          </h3>
          <p className="text-xs text-muted-foreground">Auto-triggered based on your application status</p>
        </div>
        <Link
          href="/marketplace"
          className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
        >
          View All →
        </Link>
      </div>

      <div className="space-y-4">
        {recommendations.map((rec) => (
          <div
            key={rec.id}
            className={`relative bg-gradient-to-br ${typeColors[rec.type]} p-0.5 rounded-xl overflow-hidden`}
          >
            {rec.urgent && (
              <div className="absolute top-3 right-3 z-10">
                <span className="px-3 py-1 bg-white text-coral text-xs font-black rounded-full shadow-lg flex items-center gap-1">
                  <Icon name="ExclamationTriangleIcon" size={12} />
                  URGENT
                </span>
              </div>
            )}
            
            <div className="bg-white rounded-xl p-5">
              <div className="flex items-start gap-4 mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${typeColors[rec.type]} flex items-center justify-center flex-shrink-0`}>
                  <Icon name={rec.icon as any} size={24} className="text-white" />
                </div>
                <div className="flex-1">
                  <h4 className="font-heading font-bold text-foreground mb-1">{rec.title}</h4>
                  <p className="text-sm text-muted-foreground mb-2">{rec.description}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="font-medium">{rec.provider}</span>
                    <span>•</span>
                    <div className="flex items-center gap-1">
                      <Icon name="StarIcon" size={12} className="text-warning fill-warning" />
                      <span className="font-bold text-foreground">{rec.rating}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-4">
                {rec.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="CheckCircleIcon" size={14} className="text-trust-green flex-shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between">
                <span className="text-lg font-black text-primary">{rec.price}</span>
                <a
                  href={rec.ctaLink}
                  className={`px-6 py-2.5 bg-gradient-to-r ${typeColors[rec.type]} text-white text-sm font-bold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 hover:scale-105`}
                >
                  {rec.ctaText}
                </a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}